import type { Color } from "../../common/color.js";
import type { Point } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { Label, type LabelOptions } from "./Label.js";

export interface LinkLabelOptions extends LabelOptions {
  /** Underline at rest as well as on hover. */
  alwaysUnderline?: boolean;
  /** null uses the theme's accent color. */
  linkColor?: Color | null;
  /** Color once `visited` is set. null keeps `linkColor`. */
  visitedColor?: Color | null;
  visited?: boolean;
  onClick?: () => void;
}

/**
 * A label that acts like a link.
 *
 * It raises `click` like any control; what that means — opening a page,
 * showing a dialog, jumping to a setting — is the caller's business. Focusable
 * and activated by Enter or Space too, because a link only reachable with a
 * pointer is a link half the users cannot press.
 */
export class LinkLabel extends Label {
  alwaysUnderline: boolean;
  linkColor: Color | null;
  visitedColor: Color | null;
  visited: boolean;

  constructor(options: LinkLabelOptions = {}) {
    super({ tabStop: true, cursor: "pointer", ...options });
    this.alwaysUnderline = options.alwaysUnderline ?? false;
    this.linkColor = options.linkColor ?? null;
    this.visitedColor = options.visitedColor ?? null;
    this.visited = options.visited ?? false;
    if (options.onClick) this.on("click", options.onClick);

    this.on("keydown", (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.handled = true;
      this.emit("click", {
        x: 0,
        y: 0,
        screen: { x: 0, y: 0 },
        button: 0,
        buttons: 0,
        clickCount: 1,
        ctrlKey: e.ctrlKey,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
        metaKey: e.metaKey,
        handled: false,
      });
    });
  }

  /**
   * Links are not click-through, unlike the plain labels they are built on.
   *
   * Only the text takes the hit, not the whole control rect: a label is
   * routinely wider than the words in it, and a link that swallowed clicks in
   * the empty space beside its text would be its own small mystery. Wrapped
   * text is taken as filling the width — it was broken to that width — and an
   * unattached control has nothing to measure with, so it falls back to the
   * rect it was given.
   */
  protected override hitTestSelf(p: Point): boolean {
    if (!this.text) return false;
    if (this.wrap) return true;
    const desktop = this.desktop;
    if (!desktop) return true;
    const font = this.font ?? desktop.theme.metrics.font;
    const span = this.#textExtent(desktop.rc.measureText(this.text, font).width);
    return p.x >= span.x && p.x < span.x + span.width;
  }

  /** Where a run of text this wide sits across the control, once aligned. */
  #textExtent(measured: number): { x: number; width: number } {
    const width = Math.min(measured, this.width);
    const x =
      this.textAlign === "center"
        ? (this.width - width) / 2
        : this.textAlign === "right"
          ? this.width - width
          : 0;
    return { x, width };
  }

  /** The ink for the current state, before the base class picks a color. */
  inkFor(theme: Theme): Color {
    const p = theme.palette;
    if (!this.effectivelyEnabled) return p.textDisabled;
    if (this.visited && this.visitedColor) return this.visitedColor;
    const base = this.linkColor ?? p.accent;
    return this.pressed ? p.accentPressed : this.hovered ? p.accentHover : base;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    // The base class owns alignment, wrapping and ellipsis; borrowing it by
    // setting `color` for the duration is cheaper than reimplementing all of
    // that to change one color.
    const saved = this.color;
    this.color = this.inkFor(theme);
    super.onPaint(rc, theme);
    this.color = saved;

    if (!this.alwaysUnderline && !this.hovered && !this.focused) return;
    if (!this.text) return;
    const font = this.font ?? theme.metrics.font;
    const metrics = rc.measureText(this.text, font);
    const { x, width } = this.#textExtent(metrics.width);
    // Just below the baseline of a single line of text, which is where the
    // vertical alignment puts it.
    const center =
      this.verticalAlign === "top"
        ? metrics.height / 2
        : this.verticalAlign === "bottom"
          ? this.height - metrics.height / 2
          : this.height / 2;
    const y = Math.round(center + metrics.ascent / 2 + 1);
    rc.hLine(x, y, width, this.inkFor(theme));
  }
}
